import type { RawCareGaps } from '@/features/patients/types'

/**
 * Care gaps are HEDIS measure memberships, not an open/closed list.
 *
 * Each (client, measure, year) appears exactly once with a status naming which
 * side of the measure the client falls on:
 *   Denominator — in the eligible population and the measure is NOT satisfied
 *                 → this is the open gap, the thing to act on
 *   Numerator   — the measure IS satisfied → nothing to do
 *
 * Reading `status` as a label ("shows Denominator") rather than as a verdict is
 * the mistake this module exists to prevent.
 */
export type CareGapStatus = 'open' | 'met' | 'unknown'

export interface CareGap {
  /** Measure name with the trailing year stripped, e.g. 'Colorectal Cancer Screening'. */
  measure: string
  /** Measurement year parsed from the name, or null when the name carries none. */
  year: number | null
  status: CareGapStatus
  /** The name exactly as stored, for tooltips and copy. */
  raw: string
}

const DENOMINATOR = 'denominator'
const NUMERATOR = 'numerator'

/** Measure names embed the measurement year: 'Cervical Cancer Screening 2026'. */
const TRAILING_YEAR = /\s+(\d{4})$/

function parseStatus(value: unknown): CareGapStatus {
  if (typeof value !== 'string') return 'unknown'
  const normalized = value.trim().toLowerCase()
  if (normalized === DENOMINATOR) return 'open'
  if (normalized === NUMERATOR) return 'met'
  return 'unknown'
}

function parseName(value: string): { measure: string; year: number | null } {
  const name = value.trim()
  const match = TRAILING_YEAR.exec(name)
  if (!match?.[1]) return { measure: name, year: null }
  return {
    measure: name.slice(0, match.index).trim(),
    year: Number.parseInt(match[1], 10),
  }
}

/**
 * Normalize either stored shape into one list.
 *
 * The column is polymorphic — an array of `{name, status}` for most clients and
 * a `{name: status}` object for the rest — so both are accepted here and
 * nowhere else. Entries without a usable name are dropped; entries with an
 * unrecognized status are kept as `unknown` rather than discarded, so a new
 * status value surfaces in the UI instead of vanishing.
 */
export function normalizeCareGaps(raw: RawCareGaps | null): CareGap[] {
  if (!raw) return []

  const parsed: CareGap[] = []

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue
      const name = entry.name
      if (typeof name !== 'string' || name.trim().length === 0) continue
      const { measure, year } = parseName(name)
      parsed.push({
        measure,
        year,
        status: parseStatus(entry.status),
        raw: name,
      })
    }
  } else if (typeof raw === 'object') {
    for (const [name, status] of Object.entries(raw)) {
      if (name.trim().length === 0) continue
      const { measure, year } = parseName(name)
      parsed.push({ measure, year, status: parseStatus(status), raw: name })
    }
  }

  // One row per measure-year. Both shapes can carry the same measure twice
  // (e.g. after a re-ingest); the first wins.
  const seen = new Set<string>()
  return parsed.filter((gap) => {
    const key = `${gap.measure.toLowerCase()}|${gap.year ?? ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function byMeasure(a: CareGap, b: CareGap): number {
  return a.measure.localeCompare(b.measure)
}

export interface CareGapYear {
  /** null groups the measures whose names carry no year. */
  year: number | null
  open: CareGap[]
  met: CareGap[]
  unknown: CareGap[]
}

/**
 * Group normalized gaps by measurement year, newest first, undated last.
 *
 * Grouping is not cosmetic: the same measure recurs every year, so a flat list
 * shows "Cervical Cancer Screening" two or three times with different statuses
 * and no way to tell which one is current.
 */
export function groupCareGapsByYear(gaps: CareGap[]): CareGapYear[] {
  const byYear = new Map<number | null, CareGapYear>()

  for (const gap of gaps) {
    let group = byYear.get(gap.year)
    if (!group) {
      group = { year: gap.year, open: [], met: [], unknown: [] }
      byYear.set(gap.year, group)
    }
    group[gap.status].push(gap)
  }

  for (const group of byYear.values()) {
    group.open = group.open.toSorted(byMeasure)
    group.met = group.met.toSorted(byMeasure)
    group.unknown = group.unknown.toSorted(byMeasure)
  }

  return [...byYear.values()].toSorted((a, b) => {
    if (a.year === b.year) return 0
    // Undated measures sort last in both directions.
    if (a.year === null) return 1
    if (b.year === null) return -1
    return b.year - a.year
  })
}
